/**
 * How an interactive text element signals hover.
 *
 * Colour, not an underline: hover and active go to #2563EB, eased in, and
 * nothing gains an underline it did not already have. A link underlined at
 * rest — body prose, where the underline is what marks it as a link — keeps
 * it and changes colour like everything else.
 *
 * Two variants because the two halves of the product have different grounds
 * and different contrast budgets. Measured against WCAG AA for body text
 * (4.5:1):
 *
 *   #2563EB on marketing white      5.17:1   ok
 *   #2563EB on zp-surface           4.74:1   ok
 *   #2563EB on the app's light bg   4.77:1   ok
 *   #2563EB on zp-night             3.47:1   fails — dark sections keep white
 *   #2563EB on the app's dark bg    3.61:1   fails — dark mode uses blue-400
 *   blue-400 on the app's dark bg   7.34:1   ok
 */

const EASE = "transition-colors duration-150 ease-in";

/**
 * Marketing and journal surfaces. Light grounds only; the dark sections keep
 * a white hover, because #2563EB does not clear AA on zp-night.
 */
export const LINK = `${EASE} hover:text-zp-link active:text-zp-link`;

/**
 * App surfaces, which are themed. Resting colour is the semantic foreground
 * rather than `zp-ink` — the latter is a fixed dark value and would be
 * unreadable in dark mode — and the hover lightens to blue-400 there, where
 * #2563EB is only 3.61:1.
 */
export const APP_LINK =
  `${EASE} text-foreground hover:text-zp-link active:text-zp-link ` +
  `dark:hover:text-blue-400 dark:active:text-blue-400`;

/**
 * Ink buttons — solid zp-ink, white label — share one hover: the fill goes to
 * the link blue, so a primary action answers the pointer the way a text link
 * does. White on #2563EB is 5.17:1, the same pairing as above in reverse.
 *
 * Carries `group` so an arrow inside can take `ARROW_NUDGE`.
 */
export const INK_BUTTON = `group ${EASE} hover:bg-zp-link`;

/**
 * The arrow in an ink button (or any `group`) moves a few pixels toward where
 * it points on hover. A transform, so it costs no layout; dropped under
 * reduced motion.
 */
export const ARROW_NUDGE =
  "transition-transform duration-200 ease-out group-hover:translate-x-[3px] motion-reduce:transition-none motion-reduce:group-hover:translate-x-0";
